// firestoreCache.ts
// Generic in-memory deduping cache with TTL for Firestore (or any async) fetches.

type CacheEntry<T> = {
  data?: T;
  expiresAt: number;
  promise?: Promise<T>;
};

const cache = new Map<string, CacheEntry<any>>();

/**
 * Get cached value or run fetcher (deduped) and cache it for ttlMs.
 * - key: unique cache key (string)
 * - fetcher: async function that reads Firestore and returns T
 * - ttlMs: milliseconds to keep the cached value (default 15000ms)
 */
export async function getCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs = 15_000,
  cacheBust = false
): Promise<T> {
  const now = Date.now();
  const existing = cache.get(key);
  if (!cacheBust) {

    // valid cached value -> return immediately (sync path)
    if (existing && existing.data !== undefined && existing.expiresAt > now) {
      return existing.data as T;
    }

    // if a request is already in-flight, return the same promise (dedupe)
    if (existing && existing.promise) {
      return existing.promise as Promise<T>;
    }
  }

  // otherwise, start the fetch and store the in-flight promise
  const entry: CacheEntry<T> = existing ?? { expiresAt: 0 };

  const p = (async () => {
    try {
      const result = await fetcher();
      entry.data = result;
      entry.expiresAt = Date.now() + ttlMs;
      entry.promise = undefined; // clear in-flight marker
      cache.set(key, entry);
      return result;
    } catch (err) {
      // clear promise so next caller can retry
      entry.promise = undefined;
      cache.set(key, entry);
      throw err;
    }
  })();

  entry.promise = p;
  cache.set(key, entry);
  return p;
}

/** Force invalidation for a specific key */
export function invalidate(key: string) {
  cache.delete(key);
}

/** Clear whole cache (useful for sign-out / dev) */
export function clearCache() {
  cache.clear();
}
